'use client';

import { useEffect, useMemo } from 'react';
import { usePathname } from '@/i18n/navigation';
import { isBrowser } from '@/utils/browser';
import { isInApp } from '@/libs/comeAppSDK';
import { menuTabUrl, commonPage } from '@/const/page-url';

// 需要固定高度的路由
const FIXED_HEIGHT_ROUTES = [menuTabUrl.sports, menuTabUrl.rummy, menuTabUrl.fantasy, '/casino/games/play', commonPage.gsl];

/**
 * 解决浏览器底部菜单收起问题
 */
export function useHeightControl() {
  const pathname = usePathname();

  // 检测当前路由是否不需要固定高度
  const shouldNotFixHeight = useMemo(() => {
    const currentPath = pathname || '';

    // 如果是需要固定高度的路由，返回 false（需要固定高度）
    if (FIXED_HEIGHT_ROUTES.some(route => currentPath === route || currentPath.startsWith(`${route}/`))) {
      return false;
    }

    return true;
  }, [pathname]);

  useEffect(() => {
    if (!isBrowser()) return;
    // apk内不处理高度问题
    if (isInApp()) return;

    const html = document.documentElement;
    const body = document.body;
    // 查找主布局容器（包含 main-layout id 的 div）
    const mainLayoutContainer = body.querySelector('#main-layout') as HTMLElement;

    // 如果是不需要固定高度的路由，移除固定高度样式，允许滚动
    if (shouldNotFixHeight) {
      // 保存原始样式
      const originalHtmlHeight = html.style.height;
      const originalHtmlOverflow = html.style.overflow;
      const originalBodyHeight = body.style.height;
      const originalBodyOverflow = body.style.overflow;
      const originalContainerHeight = mainLayoutContainer?.style.height || '';
      const originalContainerMinHeight = mainLayoutContainer?.style.minHeight || '';

      // 移除固定高度样式，允许滚动（使用 auto 覆盖 h-full 类）
      html.style.height = 'auto';
      html.style.overflow = '';
      body.style.height = 'auto';
      body.style.overflow = '';

      // 移除主布局容器的 h-full 样式（使用 auto 覆盖 h-full 类，保留 min-h-screen）
      if (mainLayoutContainer) {
        mainLayoutContainer.style.height = 'auto';
        // 保留 min-h-screen 的默认行为，不设置 minHeight
      }

      // 清理函数：恢复固定高度样式（移除内联样式，让 h-full 类重新生效）
      return () => {
        html.style.height = originalHtmlHeight || '';
        html.style.overflow = originalHtmlOverflow || '';
        body.style.height = originalBodyHeight || '';
        body.style.overflow = originalBodyOverflow || '';
        if (mainLayoutContainer) {
          mainLayoutContainer.style.height = originalContainerHeight || '';
          mainLayoutContainer.style.minHeight = originalContainerMinHeight || '';
        }
      };
    }
  }, [shouldNotFixHeight]);
}

